if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="search"]');
      this.form = this.querySelector('form');
      this.resultsContainer = this.querySelector('.js-results-container');
      this.btnSubmit = this.querySelector('.js-btn-submit');
      this.btnClearText = this.querySelector('.js-btn-clear-text');
      this.errorMessageContainer = this.querySelector('.js-message-error');
      this.errorMessageText = this.errorMessageContainer?.querySelector('[data-text]');
      this.colourHint = this.querySelector('[data-mh-colour-hint]');
      this.status = this.querySelector('[data-mh-search-status]');
      this.timeout = null;
      this.requestVersion = 0;
      this.isOpen = false;
      this.drawer = this.closest('.js-drawer');
      this.models = JSON.parse(this.querySelector('[data-mh-search-models]')?.textContent || '[]');
      const colours = this.querySelector('[data-mh-search-colours]');
      this.colours = colours ? JSON.parse(colours.textContent) : [];
      this.colours = Array.from(new Set([
        ...this.colours,
        ...Array.from(document.querySelectorAll('input[name="filter.v.option.color"]')).map(input => input.value)
      ]));
      if (!this.input || !this.form) return;
      this.input.addEventListener('input', () => {
        this.updateColourFilter();
        this.requestVersion++;
        this.controller?.abort();
        clearTimeout(this.timeout);
        this.isOpen = false;
        this.classList.remove('is-open');
        this.resultsContainer?.classList.add('hidden');
        if (!this.input.value.trim()) {
          this.close();
          return;
        }
        this.btnClearText?.removeAttribute('disabled');
        if (!this.resultsContainer) return;
        this.timeout = setTimeout(() => {
          this.isOpen = true;
          this.getSearchResults(this.input.value.trim());
        }, 300);
      });
      this.form.addEventListener('submit', () => {
        this.updateColourFilter();
        this.input.value = this.searchQuery(this.input.value);
      });
      this.input.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.isOpen) {
          event.preventDefault();
          event.stopPropagation();
          this.close();
        } else if (event.key === 'ArrowDown' && this.isOpen) {
          const first = this.resultLinks()[0];
          if (first) {
            event.preventDefault();
            first.focus();
          }
        }
      });
      this.resultsContainer?.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          this.close();
          this.input.focus();
          return;
        }
        if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
        const links = this.resultLinks();
        const index = links.indexOf(document.activeElement);
        if (index < 0) return;
        event.preventDefault();
        const next = index + (event.key === 'ArrowDown' ? 1 : -1);
        if (next < 0) this.input.focus();
        else links[next % links.length]?.focus();
      });
      this.btnClearText?.addEventListener('click', () => {
        this.input.value = '';
        this.updateColourFilter();
        this.close();
        this.input.focus();
      });
      this.updateColourFilter();
    }

    normalizeColour(value) {
      return String(value).normalize('NFKC').trim().toLowerCase()
        .replace(/[\s/&—–-]+/g, ' ');
    }

    matchingColours() {
      const term = this.normalizeColour(this.input.value);
      if (!term || !this.colours.some(colour => this.normalizeColour(colour) === term)) return [];
      return this.colours.filter(colour =>
        (' ' + this.normalizeColour(colour) + ' ').includes(' ' + term + ' ')
      );
    }

    searchQuery(value) {
      const words = String(value).trim().split(/\s+/);
      if (words.some(word => /[:()"*\\]/.test(word))) return value;
      const names = [...this.models, ...Array.from(this.resultsContainer?.querySelectorAll('.card-product__title') || [])
        .map(title => title.textContent.trim().split(/\s+/)[0])];
      const model = names.some(name => name.toLowerCase() === words[0]?.toLowerCase());
      if (!model || this.matchingColours().length) return value;
      return words.map(word => 'title:' + word).join(' ');
    }

    updateColourFilter() {
      this.form.querySelectorAll('[data-mh-colour-filter]').forEach(input => input.remove());
      const matches = this.matchingColours();
      matches.forEach(colour => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'filter.v.option.color';
        input.value = colour;
        input.setAttribute('data-mh-colour-filter', '');
        this.form.append(input);
      });
      if (this.colourHint) {
        this.colourHint.hidden = matches.length === 0;
        const label = this.colours.find(colour => this.normalizeColour(colour) === this.normalizeColour(this.input.value));
        const text = matches.length ? 'Colour search: ' + label + (matches.length > 1 ? ' shades' : '') : '';
        if (this.colourHint.textContent !== text) this.colourHint.textContent = text;
      }
      return matches;
    }

    resultLinks() {
      return Array.from(this.resultsContainer?.querySelectorAll('a[href]') || [])
        .filter(link => link.getClientRects().length > 0);
    }

    async getSearchResults(searchTerm) {
      const version = ++this.requestVersion;
      this.controller?.abort();
      this.controller = new AbortController();
      try {
        const params = new URLSearchParams({
          q: this.searchQuery(searchTerm),
          'resources[type]': 'product',
          'resources[limit]': '8',
          'resources[options][unavailable_products]': 'last',
          section_id: 'predictive-search'
        });
        const response = await fetch(Shopify.routes.root + 'search/suggest?' + params, {
          signal: this.controller.signal
        });
        if (!response.ok) throw new Error('Suggestions unavailable');
        const text = await response.text();
        if (!this.isOpen || version !== this.requestVersion || this.input.value.trim() !== searchTerm) return;
        const section = new DOMParser().parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Suggestions unavailable');
        this.resultsContainer.innerHTML = section.innerHTML;
        const region = this.resultsContainer.querySelector('.predictive-search__results');
        if (region) region.id = this.input.id + '-suggestion-items';
        const matches = this.matchingColours();
        const cards = Array.from(this.resultsContainer.querySelectorAll('[data-mh-product-colours]'));
        cards.forEach(card => {
          const colours = JSON.parse(card.getAttribute('data-mh-product-colours') || '[]');
          card.classList.toggle('hidden', matches.length > 0 && !colours.some(colour =>
            matches.some(match => this.normalizeColour(match) === this.normalizeColour(colour))
          ));
        });
        const count = cards.filter(card => !card.classList.contains('hidden')).length;
        if (this.status) this.status.textContent = count
          ? count + ' product suggestions. Use Tab or the arrow keys to choose, or press Enter to search.'
          : 'Press Enter to see all matching products.';
        this.open();
      } catch (error) {
        if (error.name === 'AbortError' || version !== this.requestVersion) return;
        if (this.errorMessageText) this.errorMessageText.textContent = 'Suggestions are temporarily unavailable. Press Enter to search.';
        this.errorMessageContainer?.classList.remove('hidden');
      }
    }

    open() {
      this.classList.add('is-open');
      this.resultsContainer?.classList.remove('hidden');
      this.errorMessageContainer?.classList.add('hidden');
      this.btnClearText?.removeAttribute('disabled');
    }

    close() {
      this.isOpen = false;
      this.requestVersion++;
      this.controller?.abort();
      clearTimeout(this.timeout);
      this.classList.remove('is-open');
      this.resultsContainer?.classList.add('hidden');
      this.errorMessageContainer?.classList.add('hidden');
      if (this.status) this.status.textContent = '';
      if (!this.input.value) this.btnClearText?.setAttribute('disabled', true);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
